import {
  TextField,
  Grid,
  Typography,
  Chip,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { useState, useEffect } from "react";

const belegartenOptions = [
  "Unterlagen nach § 147 Abs. 1 AO",
  "Eingangs- und Ausgangsrechnungen",
  "Aufzeichnungen über Wareneingänge und Warenausgänge",
  "Belege zu Bilanzpositionen",
  "Bewertungsunterlagen für Wirtschaftsgüter und Passiva",
  "Umsatzdaten",
  "Geschenke- und Bewirtungsbelege",
  "Verpflegungsmehraufwendungen",
  "Aufzeichnungen zu Geschäftsbeziehungen mit Konzernunternehmen",
  "Kontoauszüge",
  "Spendennachweise",
  "Dokumentationen für Buchführungs- und Datenspeicherungssysteme",
];

const defaultSelected = [
  "Unterlagen nach § 147 Abs. 1 AO",
  "Eingangs- und Ausgangsrechnungen",
  "Umsatzdaten",
  "Kontoauszüge",
  "Dokumentationen für Buchführungs- und Datenspeicherungssysteme",
];

export default function RelevanteUnterlagenForm() {
  const { control, setValue } = useFormContext();
  const [customBelegart, setCustomBelegart] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  const belegartenInfo = useWatch({
    control,
    name: "belegartenInfo",
  }) || [];

  const currentItems = Array.isArray(belegartenInfo) ? belegartenInfo : [];

  const handleToggleBelegart = (item) => {
    const alreadyAdded = currentItems.includes(item);
    if (!alreadyAdded) {
      setValue("belegartenInfo", [...currentItems, item]);
    } else {
      setValue("belegartenInfo", currentItems.filter((i) => i !== item));
    }
  };

  const handleAddCustomBelegart = () => {
    const trimmed = customBelegart.trim();
    if (trimmed === "" || currentItems.includes(trimmed)) return;
    setValue("belegartenInfo", [...currentItems, trimmed]);
    setCustomBelegart("");
  };

  const handleRemoveBelegart = (item) => {
    setValue("belegartenInfo", currentItems.filter((i) => i !== item));
  };

  const isSelected = (item) => currentItems.includes(item);

  const Beschriftung = ({ children }) => (
    <Typography variant="subtitle2" gutterBottom>
      {children}
    </Typography>
  );

  const handleLinkClick = (event) => {
    event.preventDefault();
    setOpenDialog(true);
  };

  // ✅ Vorauswahl beim ersten Laden
  useEffect(() => {
    if (currentItems.length === 0) {
      setValue("belegartenInfo", defaultSelected);
    }
  }, [currentItems.length, setValue]);

  return (
    <>
    <Grid container spacing={3} direction="column">
      <Grid item xs={12}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
      Vorkommende Belegarten im Unternehmen festlegen
              </Typography>
        <Typography variant="body1" gutterBottom>
          Bitte wählen Sie hier alle Belegarten aus, die in Ihrem Unternehmen vorkommen.
          Sie können zusätzlich eigene Belegarten ergänzen. 
          <br /><br />
          <strong>Hinweis:</strong> Die folgenden Belegarten wurden bereits vorausgewählt, da sie 
          in nahezu jedem Unternehmen vorkommen:
          <ul>
              <li>
                <Link 
                  href="https://www.gesetze-im-internet.de/ao_1977/__147.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'none' }}
                  onClick={handleLinkClick}
                >
                  Unterlagen nach § 147 Abs. 1 AO
                </Link>
              </li>
              {defaultSelected.slice(1).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          Sie können diese Auswahl bei Bedarf anpassen.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Beschriftung>Typische Belegarten auswählen</Beschriftung>
        <Grid container spacing={1}>
          {belegartenOptions.map((item, index) => (
            <Grid item key={index}>
              <Chip
                label={item}
                clickable
                color={isSelected(item) ? "primary" : "default"}
                variant={isSelected(item) ? "filled" : "outlined"}
                onClick={() => handleToggleBelegart(item)}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Beschriftung>Eigene Belegart hinzufügen</Beschriftung>
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <TextField
              label="Eigene Belegart"
              fullWidth
              value={customBelegart}
              onChange={(e) => setCustomBelegart(e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleAddCustomBelegart}
            >
              Hinzufügen
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Beschriftung>
          Im Unternehmen vorkommenden Belegarten
        </Beschriftung>
        <Controller
          name="belegartenInfo"
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <Grid container spacing={1}>
              {(Array.isArray(field.value) ? field.value : []).map((item, i) => (
                <Grid item key={i}>
                  <Chip
                    label={item}
                    color="primary"
                    onDelete={() => handleRemoveBelegart(item)}
                  />
                </Grid>
              ))}
              {(!field.value || field.value.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Belegarten ausgewählt.
                </Typography>
              )}
            </Grid>
          )}
        />
      </Grid>
    </Grid>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Unterlagen nach § 147 Abs. 1 AO
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              Unterlagen nach § 147 Abs. 1 der Abgabenordnung (AO) sind alle Aufzeichnungen und Dokumente, 
              die ein Unternehmen oder Steuerpflichtiger für steuerliche Zwecke aufbewahren muss. 
              Dieser Paragraph ist zentral für die steuerliche Buchführungspflicht in Deutschland und 
              regelt, welche Unterlagen aufbewahrungspflichtig sind.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Laut § 147 Abs. 1 AO sind folgende Unterlagen aufzubewahren:
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              1. Bücher und Aufzeichnungen
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              also z. B. Handelsbücher, Inventare, Bilanzen, GuV-Rechnungen usw.
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              2. Jahresabschlüsse
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              also insbesondere:
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              • Bilanz<br/>
              • Gewinn- und Verlustrechnung (GuV)<br/>
              • ggf. Anhang und Lagebericht<br/>
              • Eröffnungsbilanz
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              3. Arbeitsanweisungen und Organisationsunterlagen
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              die zum Verständnis dieser Unterlagen erforderlichen Arbeitsanweisungen und sonstigen Organisationsunterlagen,<br/>
              z. B. Verfahrensdokumentation zur ordnungsgemäßen Buchführung.
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              4. Buchungsbelege
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              also alle Belege, die eine Buchung begründen, z. B.:
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              • Eingangsrechnungen<br/>
              • Ausgangsrechnungen<br/>
              • Kontoauszüge<br/>
              • Quittungen<br/>
              • Kassenbons<br/>
              • Reisekostenabrechnungen
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              5. Handels- oder Geschäftsbriefe
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              • Empfangene Handels- oder Geschäftsbriefe<br/>
              • Abgesandte Handels- oder Geschäftsbriefe
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              6. Sonstige Unterlagen
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              soweit sie für die Besteuerung von Bedeutung sind, z. B.:
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 4 }}>
              • Verträge<br/>
              • Korrespondenz mit dem Finanzamt<br/>
              • Auftragsbestätigungen<br/>
              • Kalkulationen<br/>
              • E-Mails, wenn sie steuerlich relevante Inhalte haben
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Aufbewahrungsfristen gemäß § 147 AO:
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              10 Jahre für:
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              • Bücher<br/>
              • Aufzeichnungen<br/>
              • Jahresabschlüsse<br/>
              • Buchungsbelege<br/>
              • Rechnungen
            </Typography>
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              6 Jahre für:
            </Typography>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              • Handels- und Geschäftsbriefe (empfangen und abgesandt)<br/>
              • sonstige steuerlich relevante Unterlagen
            </Typography>
          </Box>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Quelle:</strong>{" "}
              <Link 
                href="https://www.gesetze-im-internet.de/ao_1977/__147.html" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                § 147 AO - Aufbewahrung von Unterlagen
              </Link>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
